import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { RiskLevelType } from "components/date-block/models";
import ActivityOffsetTag from "components/activity-offset-tag";
import { TActivitiesV3, TLinkedMaterial, TLinkedSubmittal } from "./models";
import "./style.scss";
import { getRiskAssessmentForEntity } from "./utils";
import MaterialsGroupDiffV6 from "./MaterialGroupDiffV6";
import SubmittalGroupDiffV6 from "./SubmittalGroupDiffV6";
import { EntityType } from "../../../../constants/index";
import CriticalActivityTag from "../critical-activity-tag";

function ImpactedSubmittalEntityBoxHeader({
  submittal,
  projectId,
  linkedToEndDate
}: {
  submittal: any;
  projectId: any;
  linkedToEndDate: any;
}) {
  return (
    <div
      className="w-[92%] mx-auto mt-5 pt-4 bg-white rounded-t-lg"
      style={{ border: "1px solid rgba(156, 163, 175, 0.2)" }}
    >
      <div className="font-semibold text-md mb-2 pb-2 pl-4">
        Submittal:{" "}
        <Link
          to={`/project/${projectId}/submittals/${submittal.submittal_id}`}
          className="pl-2"
          data-testid={`submittal-link-${submittal.submittal_id}`}
        >
          <i className="mr-2">
            {submittal.SDB?.old?.submittal_sequence_id} -{" "}
            {submittal.SDB?.old?.spec_no}
          </i>
          {submittal.SDB.old.title}
        </Link>
        <div className="text-right font-semibold">
          {linkedToEndDate ? "Linked with End Date" : ""}
        </div>
      </div>
    </div>
  );
}

function ImpactedMaterialEntityBoxHeader({
  material,
  projectId,
  linkedToEndDate,
  isImplicit
}: {
  material: any;
  projectId: any;
  linkedToEndDate: any;
  isImplicit: any;
}) {
  return (
    <div
      className="w-[92%] mx-auto mt-5 pt-4 bg-white rounded-t-lg"
      style={{ border: "1px solid rgba(156, 163, 175, 0.2)" }}
      data-testid="material-entity-header"
    >
      <div className="font-semibold text-md mb-2 pb-2 pl-4">
        Material:
        {material.implicit !== true ? (
          <Link
            to={`/project/${projectId}/materials/${material.material_id}`}
            className="pl-2"
            data-testid={`material-link-${material.material_id}`}
          >
            {isImplicit ? (
              `Material for Submittal ${material?.name}`
            ) : (
              <span>
                <i className="mr-2">
                  {material.MDB?.old?.material_sequence_id}
                </i>
                {material.MDB.old.name}
              </span>
            )}
          </Link>
        ) : (
          `Material for Submittal ${material?.name}`
        )}
        <div className="text-right font-semibold">
          {linkedToEndDate ? "Linked with End Date" : ""}
        </div>
      </div>
    </div>
  );
}

function ActivityInfo({
  activity,
  submittal_id,
  material_id
}: {
  activity: TActivitiesV3;
  submittal_id?: string;
  material_id?: string;
}) {
  const getActivityInfo = (entityData: any) => {
    if (!entityData) return {};

    let newActivity: any = {};
    let oldActivity: any = {};

    // Governing task is the first element in the array
    if (entityData?.old_governing_task) {
      const oldGoverningTasks: any[] = entityData?.old_governing_task;
      oldActivity = oldGoverningTasks?.length ? oldGoverningTasks[0] : null;
    }

    if (entityData?.new_governing_task) {
      const newGoverningTasks: any[] = entityData?.new_governing_task;
      newActivity = newGoverningTasks?.length ? newGoverningTasks[0] : null;
    }

    // Governing Material is the first element in the array
    if (entityData?.old_governing_material?.length) {
      const oldGoverningTask: any[] =
        entityData?.old_governing_material[0].governing_task;

      const governingTask = oldGoverningTask?.length
        ? oldGoverningTask[0]
        : null;

      oldActivity = governingTask;
    }

    if (entityData?.new_governing_material?.length) {
      const newGoverningTask: any[] =
        entityData.new_governing_material[0].governing_task;

      const governingTask = newGoverningTask?.length
        ? newGoverningTask[0]
        : null;
      newActivity = governingTask;
    }
    return {
      old_critical: oldActivity?.critical,
      old_float:
        oldActivity?.total_slack != null
          ? Math.floor(oldActivity.total_slack / 8)
          : "",
      old_offset_duration: oldActivity?.offset_duration,
      old_linked_offset_date: oldActivity?.linked_offset_date,
      linked_to_end_date: oldActivity?.linked_to_end_date,
      new_critical: newActivity?.critical,
      new_float:
        newActivity?.total_slack != null
          ? Math.floor(newActivity.total_slack / 8)
          : "",

      new_offset_duration: newActivity?.offset_duration,
      new_linked_offset_date: newActivity?.linked_offset_date
    };
  };

  let activityInfo: any = {};

  if (submittal_id && activity?.linked_submittals?.length) {
    const submittalData = activity.linked_submittals.find(
      (submittal: TLinkedSubmittal) => submittal.submittal_id === submittal_id
    );
    activityInfo = getActivityInfo(submittalData);
  } else if (material_id && activity?.linked_materials?.length) {
    const materialData = activity.linked_materials.find(
      (material: TLinkedMaterial) => material.material_id === material_id
    );

    activityInfo = getActivityInfo(materialData);
  }

  const titleDiv = (
    <span className="m-0" data-testid="activity-title">
      <span className="text-ciq-gray-700">Impacting Activity:</span>
      <i className="mr-2 pl-2" data-testid="activity-id">
        {activity.id}
      </i>
      {activity.text}
    </span>
  );
  if (activity.deleted) {
    return (
      <div className="impactingActivity" data-testid="deleted-activity-info">
        {titleDiv}
        <div className="text-right" data-testid="deleted-activity-message">
          Linked activity has been removed in the selected schedule. Please
          relink to a suitable activity, if required.
        </div>
      </div>
    );
  }

  const activityInfoDiv = (
    title1: string,
    title2: string,
    value: any,
    float: number,
    isCritical: boolean,
    isOldActivity: boolean,
    offsetDuration: any,
    linkedOffsetDate: any
  ) => {
    const parentDivClass = isOldActivity ? "bg-gray-50" : "bg-blue-50";
    const verticalLineDiv = isOldActivity ? "bg-gray-200" : "bg-blue-200";
    const floatClass = isCritical
      ? "text-black text-opacity-85 font-bold"
      : "text-black text-opacity-60 font-normal";
    return (
      <div
        className="flex flex-col space-y-1 w-full mt-6"
        data-testid={`${isOldActivity ? "before" : "after"}-activity-info`}
      >
        <div className="flex justify-between items-end w-full h-6">
          <div className="text-black text-opacity-60 font-medium text-xs">
            {title1}
          </div>
          {isCritical && (
            <div data-testid="critical-activity-tag-container">
              <CriticalActivityTag data-testid="critical-activity-tag" />
            </div>
          )}
        </div>
        <div
          className={`flex justify-between w-full text-sm h-[38px] ${parentDivClass}`}
        >
          <div className="flex items-center space-x-2 w-full px-2">
            <div className={`-ml-2 h-full w-1 ${verticalLineDiv}`} />
            <div className="flex truncate space-x-1">
              <span className="text-ciq-gray-800 font-medium ">{title2}:</span>{" "}
              <span className="text-black text-opacity-60 font-normal">
                {DateUtils.format(value, DATE_FORMAT_MMDDYYYY)}
              </span>
              {offsetDuration && (
                <span data-testid="activity-offset-container">
                  <ActivityOffsetTag
                    task={{
                      offset_duration: offsetDuration,
                      linked_offset_date: linkedOffsetDate
                    }}
                    isShowDate
                    data-testid="activity-offset-tag"
                  />
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 mr-2 h-full">
            <div className="text-ciq-gray-800 font-medium">Float:</div>
            <div className={floatClass}>{float}</div>
          </div>
        </div>
      </div>
    );
  };

  const beforeDate = activityInfo?.linked_to_end_date
    ? activity?.old_end_date
    : activity?.old_start;

  const afterDate = activityInfo?.linked_to_end_date
    ? activity?.new_end_date
    : activity?.new_start;

  const delta = activityInfo?.linked_to_end_date
    ? activity?.end_date_delta
    : activity?.delta;

  return (
    <div className="flex flex-col" data-testid="activity-info-container">
      {titleDiv}
      <div className="grid grid-cols-5 gap-2">
        <div className="flex col-start-2 col-end-5 space-x-6">
          {activityInfoDiv(
            "Before",
            "Old Activity Date",
            beforeDate,
            activityInfo.old_float,
            activityInfo.old_critical,
            true,
            activityInfo?.old_offset_duration,
            activityInfo?.old_linked_offset_date
          )}
          {activityInfoDiv(
            "After",
            "New Activity Date",
            afterDate,
            activityInfo.new_float,
            activityInfo.new_critical,
            false,
            activityInfo?.new_offset_duration,
            activityInfo?.new_linked_offset_date
          )}

          <div
            className="flex-col font-medium mt-7"
            data-testid="delta-section"
          >
            <div
              className="text-xs text-black text-opacity-60 mt-1"
              data-testid="delta-label"
            >
              Delta
            </div>
            <div
              className="flex space-x-1 text-sm text-black text-opacity-85 mt-3"
              data-testid="delta-value"
            >
              <span>{delta}</span>
              <span>
                {(delta && delta > 1) || (delta && delta < -1) ? "days" : "day"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleImpactV3({
  activity,
  entityType
}: {
  activity: TActivitiesV3;
  entityType: EntityType;
}) {
  const { projectId } = useParams() as any;
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div data-testid="schedule-impact-v3-container">
      {entityType === EntityType.Submittal &&
        (activity.linked_submittals || []).map(
          (submittal: TLinkedSubmittal, i: number) => {
            const riskAssessment = getRiskAssessmentForEntity(submittal?.SDB);
            const key = `${i}-${submittal.submittal_id}`;
            const linkedToEndDate =
              submittal.old_governing_task?.find(
                (x) => x.activity_id === activity.id
              )?.linked_to_end_date || false;

            return (
              <div key={key}>
                <ImpactedSubmittalEntityBoxHeader
                  submittal={submittal}
                  projectId={projectId}
                  linkedToEndDate={linkedToEndDate}
                />
                <div
                  className="w-[92%] mx-auto pt-4 bg-white rounded-b-lg mb-5"
                  style={{ border: "1px solid rgba(156, 163, 175, 0.2)" }}
                  data-testid={`submittal-content-${submittal.submittal_id}`}
                >
                  <div className="impactBoxContainerContent">
                    {riskAssessment ? (
                      <div
                        data-testid={`submittal-risk-assessment-${submittal.submittal_id}`}
                      >
                        <span
                          className="mr-1 text-ciq-gray-700"
                          data-testid="risk-assessment-label"
                        >
                          Risk assessment after Schedule Change:
                        </span>
                        <span
                          className={
                            riskAssessment.level === RiskLevelType.High
                              ? "text-ciq-orange-800"
                              : ""
                          }
                          data-testid="risk-assessment-text"
                        >
                          {riskAssessment.text}
                        </span>
                      </div>
                    ) : null}
                    <ActivityInfo
                      activity={activity}
                      submittal_id={submittal.submittal_id}
                    />
                  </div>
                  <div
                    className="impactBoxContainerContent"
                    ref={containerRef}
                    data-testid={`submittal-diff-container-${submittal.submittal_id}`}
                  >
                    <SubmittalGroupDiffV6
                      submittal={submittal}
                      activityComparison={activity}
                      projectId={projectId}
                    />
                  </div>
                </div>
              </div>
            );
          }
        )}

      {entityType === EntityType.Material &&
        (activity.linked_materials || []).map(
          (material: TLinkedMaterial, i: number) => {
            const riskAssessment = getRiskAssessmentForEntity(material?.MDB);
            const key = `${i}-${material.material_id}`;
            const linkedToEndDate =
              material.old_governing_task?.find(
                (x) => x.activity_id === activity.id
              )?.linked_to_end_date || false;

            const isImplicit = material.MDB.old?.implicit;

            return (
              <div
                key={key}
                data-testid={`material-container-${material.material_id}`}
              >
                <ImpactedMaterialEntityBoxHeader
                  material={material}
                  projectId={projectId}
                  linkedToEndDate={linkedToEndDate}
                  isImplicit={isImplicit}
                />
                <div
                  key={key}
                  className="w-[92%] mx-auto pt-4 bg-white rounded-b-lg mb-5"
                  style={{ border: "1px solid rgba(156, 163, 175, 0.2)" }}
                >
                  <div className="impactBoxContainerContent">
                    {riskAssessment ? (
                      <div
                        data-testid={`material-risk-assessment-${material.material_id}`}
                      >
                        <span
                          className="mr-1 text-ciq-gray-700"
                          data-testid="material-risk-assessment-label"
                        >
                          Risk assessment:
                        </span>
                        <span
                          className={
                            riskAssessment.level === RiskLevelType.High
                              ? "text-ciq-orange-800"
                              : ""
                          }
                          data-testid="material-risk-assessment-text"
                        >
                          {riskAssessment.text}
                        </span>
                      </div>
                    ) : null}

                    <ActivityInfo
                      activity={activity}
                      material_id={material.material_id}
                    />
                  </div>
                  <div
                    className="impactBoxContainerContent"
                    ref={containerRef}
                    data-testid={`material-diff-container-${material.material_id}`}
                  >
                    <MaterialsGroupDiffV6
                      material={material}
                      activityComparison={activity}
                      projectId={projectId}
                      data-testid={`material-group-diff-${material.material_id}`}
                    />
                  </div>
                </div>
              </div>
            );
          }
        )}
    </div>
  );
}
export default ScheduleImpactV3;
